import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check } from "lucide-react";
import { notificationsApi, type NotificationDto } from "@api/modules/notifications.api";
import { queryKeys } from "@api/queryKeys";
import { useNotificationStore } from "@stores/notifications/notificationStore";
import { formatDateTime } from "@lib/formatters";

const PREVIEW_COUNT = 6;

export const NotificationBell: React.FC = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const containerRef = useRef<HTMLDivElement>(null);
    const unreadCount = useNotificationStore((s) => s.unreadCount);

    const { data, isLoading } = useQuery({
        queryKey: ["notifications", "preview"],
        queryFn: () => notificationsApi.list({ perPage: PREVIEW_COUNT }),
        enabled: isOpen,
    });

    const markReadMutation = useMutation({
        mutationFn: (id: string) => notificationsApi.markRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
            queryClient.invalidateQueries({ queryKey: ["notifications", "preview"] });
            void useNotificationStore.getState().fetchUnreadCount();
        },
    });
    const markRead = (n: NotificationDto) => {
        if (!n.readAt) markReadMutation.mutate(n.id);
    };

    useEffect(() => {
        if (!isOpen) return;
        const onClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [isOpen]);

    const openNotification = (n: NotificationDto) => {
        markRead(n);
        setIsOpen(false);
        if (n.url) {
            navigate(n.url);
        } else {
            navigate("/notifications");
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen((v) => !v)}
                aria-label={t("notifications.title")}
                className="relative rounded-xl p-2 text-neutral-ink dark:text-white hover:bg-neutral-light/70 dark:hover:bg-white/10 border border-neutral-border dark:border-white/10 transition-colors shadow-sm"
            >
                <Bell size={19} />
                {unreadCount > 0 && (
                    <span className="absolute -end-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-gold px-1 text-[9px] font-bold text-neutral-ink shadow-sm font-mono">
                        {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute end-0 z-50 mt-2 w-96 sm:w-[420px] max-w-[95vw] rounded-2xl border border-neutral-border bg-white shadow-mz-xl dark:border-white/10 dark:bg-neutral-navy backdrop-blur-2xl overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent pointer-events-none" />
                    <div className="flex items-center justify-between border-b border-neutral-border/50 px-5 py-3.5 dark:border-white/10">
                        <span className="font-bold text-sm text-neutral-ink dark:text-white font-display">
                            {t("notifications.title")}
                        </span>
                        {unreadCount > 0 && (
                            <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-gold-dark dark:text-gold-light bg-gold/10 px-2 py-0.5 rounded-full border border-gold/30">
                                {unreadCount} new
                            </span>
                        )}
                    </div>
                    <div className="max-h-84 overflow-y-auto divide-y divide-neutral-border/40 dark:divide-white/5">
                        {isLoading ? (
                            <p className="px-5 py-8 text-center text-xs text-neutral-secondary">
                                {t("common.loading")}
                            </p>
                        ) : !data || data.notifications.length === 0 ? (
                            <p className="px-5 py-8 text-center text-xs text-neutral-secondary">
                                {t("notifications.noNotifications")}
                            </p>
                        ) : (
                            data.notifications.map((n) => (
                                <button
                                    key={n.id}
                                    type="button"
                                    onClick={() => openNotification(n)}
                                    className={`flex w-full items-start gap-3 px-5 py-3.5 text-start hover:bg-neutral-light/50 dark:hover:bg-white/[0.04] transition-colors ${
                                        n.readAt ? "opacity-60" : ""
                                    }`}
                                >
                                    {!n.readAt && (
                                        <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-gold" />
                                    )}
                                    <div className="min-w-0 flex-1 space-y-0.5">
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="block truncate text-sm font-bold text-neutral-ink dark:text-white">
                                                {n.title}
                                            </span>
                                            {n.createdAt && (
                                                <span className="shrink-0 text-[10px] font-mono text-neutral-secondary">
                                                    {formatDateTime(n.createdAt)}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-neutral-secondary dark:text-slate-300 leading-relaxed">
                                            {n.message}
                                        </p>
                                    </div>
                                    {!n.readAt && (
                                        <Check size={14} className="mt-1 flex-shrink-0 text-gold" />
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setIsOpen(false);
                            navigate("/notifications");
                        }}
                        className="block w-full border-t border-neutral-border/50 dark:border-white/10 px-4 py-3 text-center text-xs font-bold tracking-wider uppercase text-primary dark:text-gold-light hover:bg-neutral-light/50 dark:hover:bg-white/5 transition-colors"
                    >
                        {t("common.viewAll")}
                    </button>
                </div>
            )}
        </div>
    );
};
